/**
 * Task #3414 — Commissioning Agent AI: modal di configurazione (card agenti di
 * sistema in AI Tools). Pattern semplificato di WorkflowBuilderAgentModal:
 * tab "Modello AI" (provider/modello/parametri su service_key
 * `commissioning_builder`, endpoint GET/PUT /incentivazione/agent/config) +
 * tab "Agente" con descrizione capacità e governance (read-only).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, Trophy, ShieldCheck, FileText, Recycle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { useChatModelOptions } from '@/hooks/useChatModelOptions';

const COMMISSIONING_BUILDER_SERVICE_KEY = 'commissioning_builder';

type ProviderUi = 'openai' | 'claude' | 'gemini';
const KNOWN_UI_PROVIDERS: readonly ProviderUi[] = ['openai', 'claude', 'gemini'];

interface AvailableProviderEntry {
  id: string;
  label: string;
  kind: 'canonical' | 'custom';
  capability_supported: boolean;
  is_valid: boolean;
  disabled_reason: string | null;
  reason_code?: string | null;
  health_status?: string;
}

interface ProviderOption {
  value: ProviderUi;
  label: string;
}

function providerToDb(p: string): string {
  return p === 'gemini' ? 'google' : p;
}
function backendIdToUi(id: string): ProviderUi | null {
  const v = id === 'google' ? 'gemini' : id;
  return (KNOWN_UI_PROVIDERS as readonly string[]).includes(v) ? (v as ProviderUi) : null;
}
function providerFromDb(p: string): ProviderUi {
  return backendIdToUi(p) ?? 'openai';
}
function stripProxySuffix(label: string): string {
  return label.replace(/\s*\(proxy\)\s*$/i, '').trim();
}

interface CommissioningAgentConfigDto {
  model: string;
  provider: string;
  temperature: number;
  maxTokens: number;
}

function CommissioningAgentConfigCard() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [providerUI, setProviderUI] = useState<ProviderUi>('openai');
  const [modelName, setModelName] = useState<string>('');
  const [temperature, setTemperature] = useState<number>(0.7);
  const [maxTokens, setMaxTokens] = useState<number>(4000);
  const [currentBadge, setCurrentBadge] = useState<{ model: string; provider: string } | null>(null);
  const [providerOptions, setProviderOptions] = useState<ProviderOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<{ providers: AvailableProviderEntry[] }>(
          `/ai-tools/available-providers?service_key=${COMMISSIONING_BUILDER_SERVICE_KEY}`
        );
        const list = res.success && res.data ? res.data.providers ?? [] : [];
        const mapped = list
          .filter((e) => e.kind === 'canonical' && e.capability_supported)
          .reduce<ProviderOption[]>((acc, e) => {
            const value = backendIdToUi(e.id);
            if (value && !acc.some((o) => o.value === value)) {
              acc.push({ value, label: stripProxySuffix(e.label) });
            }
            return acc;
          }, []);
        if (!cancelled) setProviderOptions(mapped);
      } catch (err) {
        console.error('[CommissioningAgentConfigCard] providers load error:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const modelsQuery = useChatModelOptions(providerUI);
  const availableModels = modelsQuery.data?.models ?? [];
  const modelsLoading = modelsQuery.isLoading;

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<CommissioningAgentConfigDto>('/incentivazione/agent/config');
      if (res.success && res.data) {
        const ui = providerFromDb(res.data.provider || 'openai');
        setProviderUI(ui);
        setModelName(res.data.model || '');
        setTemperature(typeof res.data.temperature === 'number' ? res.data.temperature : 0.7);
        setMaxTokens(typeof res.data.maxTokens === 'number' ? res.data.maxTokens : 4000);
        setCurrentBadge({ model: res.data.model, provider: ui });
      }
    } catch (err) {
      console.error('[CommissioningAgentConfigCard] load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleProviderChange = (uiProvider: string) => {
    const next = providerFromDb(uiProvider);
    setProviderUI(next);
    setModelName('');
  };

  const save = async () => {
    if (!modelName.trim()) {
      toast({ title: 'Modello richiesto', description: 'Seleziona un modello', variant: 'destructive' });
      return;
    }
    if (temperature < 0 || temperature > 1.5) {
      toast({ title: 'Temperature fuori range', description: 'Valori ammessi: 0 – 1.5', variant: 'destructive' });
      return;
    }
    if (!Number.isFinite(maxTokens) || maxTokens <= 0 || maxTokens > 32000) {
      toast({ title: 'Max tokens fuori range', description: 'Valori ammessi: 1 – 32000', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await apiClient.put('/incentivazione/agent/config', {
        provider: providerToDb(providerUI),
        model: modelName.trim(),
        temperature: Number(temperature),
        maxTokens: Math.floor(maxTokens),
      });
      setCurrentBadge({ model: modelName.trim(), provider: providerUI });
      toast({
        title: 'Configurazione salvata',
        description: `${providerUI} · ${modelName} · temp ${temperature} · max ${maxTokens}`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Impossibile salvare la configurazione';
      toast({ title: 'Errore salvataggio', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border/50">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Trophy className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-base">Commissioning Agent</CardTitle>
              <CardDescription className="text-xs">
                Provider, modello e parametri usati dall&apos;agente Commissioning (hub Incentivazione).
                Salvati in <code className="text-[10px]">platform_model_config.extra_config</code> ·
                service_key=<code>commissioning_builder</code>.
              </CardDescription>
            </div>
          </div>
          {currentBadge && (
            <Badge
              variant="outline"
              className="shrink-0 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-[11px]"
            >
              <span className="font-mono mr-1">{currentBadge.model}</span>· {currentBadge.provider}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Provider</label>
              <Select value={providerUI} onValueChange={handleProviderChange} disabled={saving}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providerOptions.length === 0 && (
                    <SelectItem value={providerUI} className="text-xs">
                      {providerUI}
                    </SelectItem>
                  )}
                  {providerOptions.map((p) => (
                    <SelectItem key={p.value} value={p.value} className="text-xs">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Modello
                {modelsLoading && <Loader2 className="h-3 w-3 inline ml-2 animate-spin" />}
              </label>
              <Select value={modelName} onValueChange={setModelName} disabled={saving || modelsLoading}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder={modelName || 'Seleziona modello'} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {availableModels.length === 0 && modelName && (
                    <SelectItem value={modelName} className="text-xs">
                      {modelName}
                    </SelectItem>
                  )}
                  {availableModels.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-xs">
                      {m.name || m.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Temperature ({temperature.toFixed(2)})
              </label>
              <Input
                type="number"
                min={0}
                max={1.5}
                step={0.1}
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                disabled={saving}
                className="h-9 text-xs"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                0 = deterministico · 1.5 = molto creativo (default 0.7)
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Max Tokens</label>
              <Input
                type="number"
                min={1}
                max={32000}
                step={100}
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value, 10))}
                disabled={saving}
                className="h-9 text-xs"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Limite token risposta (default 4000, max 32000)
              </p>
            </div>
          </div>
        )}
        <div className="flex justify-end pt-2 border-t">
          <Button onClick={save} disabled={saving || loading} size="sm">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva configurazione
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface CommissioningAgentConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CommissioningAgentConfigModal({ open, onOpenChange }: CommissioningAgentConfigModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden sm:flex sm:flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Commissioning Agent AI
          </DialogTitle>
          <DialogDescription>
            Agente di sistema dell&apos;hub Incentivazione: assiste la creazione di gare, configuratori,
            functions e analizza le lettere gara PDF.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="model" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="model">Modello AI</TabsTrigger>
            <TabsTrigger value="agent">Agente</TabsTrigger>
          </TabsList>

          <TabsContent value="model" className="flex-1 overflow-auto space-y-4 mt-4">
            <CommissioningAgentConfigCard />
          </TabsContent>

          <TabsContent value="agent" className="flex-1 overflow-auto space-y-3 mt-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <FileText className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium">Analisi lettera gara PDF</h4>
                    <p className="text-xs text-muted-foreground">
                      Allegando una lettera gara nel chat modal, l&apos;agente estrae piste/soglie/valori e
                      propone la mappatura completa (template, functions, liste, DAG). Le entità vengono
                      create in stato <Badge variant="outline" className="text-[10px]">draft</Badge> solo
                      dopo la tua approvazione.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Recycle className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium">Riuso dal catalogo</h4>
                    <p className="text-xs text-muted-foreground">
                      Prima di creare nuove functions/template/liste, l&apos;agente cerca nel catalogo
                      esistente e motiva ogni nuova creazione (nota di riuso nella proposta).
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium">Governance brand-pushed</h4>
                    <p className="text-xs text-muted-foreground">
                      Le entità gestite dal brand (managed) non sono modificabili: l&apos;agente propone la
                      duplicazione personalizzata. Sulle gare attive ogni modifica include la proposta di
                      ricalcolo esplicito, mai silenzioso. Il modal si apre dalla toolbar di ogni tab
                      dell&apos;hub Incentivazione.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
